import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

import { dayjs } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";

/*
 * The range picker Ant Design's RangePicker is shaped like: one field holding
 * both ends, a popover with shortcuts down the left and two months side by
 * side, and the span between the ends highlighted as you hover.
 *
 * BUILT RATHER THAN INSTALLED. Pulling antd back in for one control would
 * reintroduce the design system this whole rewrite exists to remove — two sets
 * of tokens, two theming stories, and about 400 kB for a calendar. react-day-
 * picker was the other candidate; it brings date-fns, and the app already
 * carries dayjs, so the month arithmetic here is a dozen lines rather than a
 * second date library.
 *
 * A range needs TWO months visible. With one, picking "26 July to 3 August"
 * means choosing a day, paging forward, and hoping you remember where you
 * started — and the hover preview, which is what makes the span legible, has
 * nothing to draw across.
 */

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const DEFAULT_PRESETS = [
    { label: "Today", days: 0 },
    { label: "Last 7 Days", days: 6 },
    { label: "Last 14 Days", days: 13 },
    { label: "Last 30 Days", days: 29 },
    { label: "Last 90 Days", days: 89 },
];

/** The 42 cells of a month grid: leading blanks, the month, trailing blanks. */
function monthGrid(month) {
    const first = month.startOf("month");
    const start = first.subtract(first.day(), "day");
    return Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
}

export default function DateRangePicker({
    startDate,
    endDate,
    onChange,
    max,
    min,
    presets = DEFAULT_PRESETS,
    className,
    id,
}) {
    const [open, setOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(() => dayjs(startDate || undefined).startOf("month"));

    // The half-made selection: the first click, before the second lands.
    const [pending, setPending] = useState(null);
    const [hovered, setHovered] = useState(null);

    const rootRef = useRef(null);
    const popoverRef = useRef(null);

    /*
     * WHICH EDGE TO HANG FROM.
     *
     * Two months and a shortcut column are about 770px wide, and this control
     * usually sits at the right-hand end of a filter bar — so anchored left it
     * ran off the window and the second month was simply not there. Measured
     * once on open, because the answer depends on where the trigger happens to
     * be, not on a breakpoint.
     */
    const [alignRight, setAlignRight] = useState(false);

    useLayoutEffect(() => {
        if (!open || !popoverRef.current || !rootRef.current) return;

        const trigger = rootRef.current.getBoundingClientRect();
        const width = popoverRef.current.offsetWidth;
        const margin = 8;

        // Flip only if flipping actually helps: on a narrow window both edges
        // overflow, and right-aligned at least keeps the shortcuts visible.
        setAlignRight(
            trigger.left + width + margin > window.innerWidth &&
                trigger.right - width - margin >= 0,
        );
    }, [open]);

    /*
     * Close on an outside click or Escape, and reset the half-made selection —
     * leaving `pending` behind would make the NEXT open start mid-range, so the
     * first click would silently become an end date.
     */
    useEffect(() => {
        if (!open) return;

        const onDown = (e) => {
            if (!rootRef.current?.contains(e.target)) {
                setOpen(false);
                setPending(null);
            }
        };
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            setOpen(false);
            setPending(null);
        };

        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // Reopening should show the range you are looking at, not wherever you last
    // paged to.
    const show = (next) => {
        if (next) setViewMonth(dayjs(startDate || undefined).startOf("month"));
        setOpen(next);
        setPending(null);
    };

    const months = useMemo(() => [viewMonth, viewMonth.add(1, "month")], [viewMonth]);

    const isDisabled = (day) => {
        if (max && day.isAfter(dayjs(max), "day")) return true;
        if (min && day.isBefore(dayjs(min), "day")) return true;
        return false;
    };

    /*
     * While a range is half-made, the highlight follows the cursor — that
     * preview is the whole reason two months are on screen, because it is how
     * you see the span before committing to it.
     */
    const previewRange = () => {
        if (!pending) return [startDate, endDate];
        const other = hovered ?? pending;
        return pending <= other ? [pending, other] : [other, pending];
    };

    const [rangeFrom, rangeTo] = previewRange();

    const pick = (day) => {
        const value = day.format("YYYY-MM-DD");

        if (!pending) {
            setPending(value);
            setHovered(value);
            return;
        }

        // Second click completes it, in whichever order the two were clicked.
        const [from, to] = pending <= value ? [pending, value] : [value, pending];
        setPending(null);
        setOpen(false);
        onChange({ startDate: from, endDate: to });
    };

    const applyPreset = (days) => {
        const end = dayjs();
        onChange({
            startDate: end.subtract(days, "day").format("YYYY-MM-DD"),
            endDate: end.format("YYYY-MM-DD"),
        });
        setPending(null);
        setOpen(false);
    };

    const activePreset = (days) => {
        const end = dayjs();
        return (
            startDate === end.subtract(days, "day").format("YYYY-MM-DD") &&
            endDate === end.format("YYYY-MM-DD")
        );
    };

    return (
        <div ref={rootRef} className={cx("relative", className)}>
            {/* ---- The field ---- */}
            <button
                type="button"
                id={id}
                onClick={() => show(!open)}
                aria-haspopup="dialog"
                aria-expanded={open}
                data-range-trigger
                className={cx(
                    "flex h-9 w-full items-center gap-2 rounded-md border bg-white px-3 text-[13px] transition dark:bg-slate-900",
                    open
                        ? "border-brand-500 ring-2 ring-brand-500/20"
                        : "border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600",
                )}
            >
                <span
                    className={cx(
                        "font-mono tabular-nums",
                        pending ? "text-brand-600 dark:text-brand-400" : "",
                    )}
                    data-range-from-label
                >
                    {rangeFrom || "Start"}
                </span>
                <ChevronRight className="size-3.5 shrink-0 text-slate-400" strokeWidth={2} />
                <span className="font-mono tabular-nums" data-range-to-label>
                    {rangeTo || "End"}
                </span>
            </button>

            {/* ---- The popover ---- */}
            {open && (
                <div
                    ref={popoverRef}
                    role="dialog"
                    aria-label="Choose a date range"
                    data-range-popover
                    className={cx(
                        "absolute top-full z-50 mt-1.5 flex w-max overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900",
                        alignRight ? "right-0" : "left-0",
                    )}
                >
                    {/* Shortcuts. Most range questions are "the last N days", and
                        counting back by hand is the part people get wrong. */}
                    <div className="w-36 shrink-0 border-r border-slate-200 py-2 dark:border-slate-800">
                        {presets.map((preset) => (
                            <button
                                key={preset.label}
                                type="button"
                                data-preset={preset.label}
                                aria-pressed={activePreset(preset.days)}
                                onClick={() => applyPreset(preset.days)}
                                className={cx(
                                    "block w-full px-3.5 py-2 text-left text-[12.5px] transition",
                                    activePreset(preset.days)
                                        ? "bg-brand-50 font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-300"
                                        : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800",
                                )}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>

                    <div className="p-3">
                        {/*
                         * The header is exactly as wide as the calendars: two
                         * w-56 halves with the arrows tucked inside them.
                         * Hanging the arrows OUTSIDE made this row wider than
                         * the months below it, and the difference showed as a
                         * band of empty popover down the right-hand side.
                         */}
                        <div className="mb-2 flex gap-4" data-months-header>
                            <div className="flex w-56 items-center justify-between">
                                <span className="flex gap-0.5">
                                    <NavButton
                                        label="Previous year"
                                        onClick={() => setViewMonth((m) => m.subtract(1, "year"))}
                                    >
                                        <ChevronsLeft className="size-4" strokeWidth={2} />
                                    </NavButton>
                                    <NavButton
                                        label="Previous month"
                                        onClick={() => setViewMonth((m) => m.subtract(1, "month"))}
                                    >
                                        <ChevronLeft className="size-4" strokeWidth={2} />
                                    </NavButton>
                                </span>
                                <span className="text-[13px] font-semibold whitespace-nowrap">
                                    {months[0].format("MMM YYYY")}
                                </span>
                                <span className="w-14" aria-hidden="true" />
                            </div>

                            <div className="flex w-56 items-center justify-between">
                                <span className="w-14" aria-hidden="true" />
                                <span className="text-[13px] font-semibold whitespace-nowrap">
                                    {months[1].format("MMM YYYY")}
                                </span>
                                <span className="flex gap-0.5">
                                    <NavButton
                                        label="Next month"
                                        onClick={() => setViewMonth((m) => m.add(1, "month"))}
                                    >
                                        <ChevronRight className="size-4" strokeWidth={2} />
                                    </NavButton>
                                    <NavButton
                                        label="Next year"
                                        onClick={() => setViewMonth((m) => m.add(1, "year"))}
                                    >
                                        <ChevronsRight className="size-4" strokeWidth={2} />
                                    </NavButton>
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-4" data-calendars onMouseLeave={() => setHovered(null)}>
                            {months.map((month) => (
                                <Month
                                    key={month.format("YYYY-MM")}
                                    month={month}
                                    from={rangeFrom}
                                    to={rangeTo}
                                    onPick={pick}
                                    onHover={setHovered}
                                    isDisabled={isDisabled}
                                />
                            ))}
                        </div>

                        <p className="mt-2 border-t border-slate-100 pt-2 text-[11.5px] text-slate-400 dark:border-slate-800 dark:text-slate-500">
                            {pending
                                ? "Now pick the other end — either direction works."
                                : "Pick a start and an end."}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

function NavButton({ label, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className="grid size-7 place-items-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
            {children}
        </button>
    );
}

function Month({ month, from, to, onPick, onHover, isDisabled }) {
    const days = monthGrid(month);

    // 7 × size-8 = 224px. Stated, because a grid track is a FRACTION of its
    // container: left to itself it shrinks below the cell size and the days
    // collapse into each other.
    return (
        <div className="w-56 shrink-0">
            <div className="mb-1 grid grid-cols-7">
                {WEEKDAYS.map((d) => (
                    <span
                        key={d}
                        className="grid size-8 place-items-center text-[11px] font-medium text-slate-400 dark:text-slate-500"
                    >
                        {d}
                    </span>
                ))}
            </div>

            <div className="grid grid-cols-7">
                {days.map((day) => {
                    const iso = day.format("YYYY-MM-DD");
                    const outside = day.month() !== month.month();
                    const disabled = isDisabled(day);

                    const isFrom = iso === from;
                    const isTo = iso === to;
                    const inside = from && to && iso > from && iso < to;
                    const isEnd = isFrom || isTo;

                    return (
                        <button
                            key={iso}
                            type="button"
                            data-day={iso}
                            disabled={disabled}
                            onClick={() => onPick(day)}
                            onMouseEnter={() => onHover(iso)}
                            className={cx(
                                "grid size-8 place-items-center text-[12.5px] transition",
                                // The band between the ends, drawn as a
                                // full-width cell so consecutive days join up.
                                inside && "bg-brand-50 dark:bg-brand-950/60",
                                isFrom && to !== from && "rounded-l-md",
                                isTo && to !== from && "rounded-r-md",
                                isEnd
                                    ? "bg-brand-600 font-semibold text-white"
                                    : disabled
                                      ? "cursor-not-allowed text-slate-300 dark:text-slate-700"
                                      : outside
                                        ? "text-slate-300 hover:bg-slate-100 dark:text-slate-600 dark:hover:bg-slate-800"
                                        : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
                            )}
                        >
                            {day.date()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
